import {
  Component, OnInit, ViewChild, AfterViewInit, ElementRef, OnDestroy, SimpleChange } from '@angular/core'
import { lineChartStyle3Lines } from 'src/app/utils/line-chart-style'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/reducers'
import { isEventDrawerOpened } from 'src/app/selectors/shell.selector'
import { GoogleChartComponent, ChartSelectionChangedEvent } from 'angular-google-charts'
import { openEventDrawer } from 'src/app/actions/shell.actions'
import { selectEventDate } from 'src/app/actions/event.actions'
import { getSelectedEventDate } from 'src/app/selectors/event.selector'
import { Subscription, Observable } from 'rxjs'
import * as moment from 'moment'
import { getUnits, getLoading, getUnitsCsv } from '../../../selectors/units-graph.selector'
import { map, take } from 'rxjs/operators'
import { range } from 'src/app/utils/range'
import {
  getDataCollectByUnitsGraph,
  getDataCollectLoading,
} from 'src/app/selectors/data-collections.selector'
import { Artist } from 'src/app/models/artist.model'
import { getSelectedArtist } from 'src/app/selectors/artist.selector'
import { MediaObserver, MediaChange } from '@angular/flex-layout'
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout'
import { of } from 'rxjs'
import { Units } from 'src/app/models/units.model'

@Component({
    selector: 'app-units-graph',
    templateUrl: './units-graph.component.html',
    styleUrls: ['./units-graph.component.scss'],
    standalone: false
})
export class UnitsGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chart')
  chart: GoogleChartComponent

  @ViewChild('container')
  container: ElementRef

  private widthOpened = false
  private widthWhenOpened = 0
  private widthWhenClosed = 0

  units$: Observable<any[]>
  isLoading$: Observable<boolean>

  dataCollections$: Observable<boolean>
  dataCollectLoading$: Observable<boolean>

  subAccounts$: Observable<string[]>

  unitsOptions = lineChartStyle3Lines

  private subscriptions: Subscription[] = []

  csvId = 'unit'
  csvData$: Observable<any[]>
  csvFileName = 'アルバム換算数'
  selectedArtist$: Observable<Artist>

  isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(map(it => it.matches))

  checkedSubAccounts: any[]
  checkLabels: any[]
  colorsOption: any[]

  constructor(
    private store: Store<AppState>,
    public media: MediaObserver,
    private breakpointObserver: BreakpointObserver
  ) {
    this.subscriptions.push(media.asObservable().subscribe(() => {}))
  }

  ngOnInit() {
    this.isLoading$ = this.store.select(getLoading)
    this.getIniData()
    this.subscriptions.push(
      this.isLoading$.subscribe(it => {
        if (it) {
          this.getIniData()
        }
      })
    )

    this.dataCollections$ = this.store.select(getDataCollectByUnitsGraph)
    this.dataCollectLoading$ = this.store.select(getDataCollectLoading)
    this.csvData$ = this.store.select(getUnitsCsv)
    this.selectedArtist$ = this.store.select(getSelectedArtist)

    this.subscriptions.push(
      this.store.select(isEventDrawerOpened).subscribe(opened => {
        if (!this.chart) {
          return
        }
        this.widthOpened = opened
        this.chart.width = opened ? this.widthWhenOpened : this.widthWhenClosed
        this.redrawChart()
      })
    )

    this.subscriptions.push(
      this.store.select(getSelectedEventDate).subscribe(date => {
        if (!this.chart) {
          return
        }

        const googleChart = this.chart.chart

        if (!googleChart) {
          return
        }

        if (!date) {
          googleChart.setSelection([])
          return
        }

        const ymd = moment(date).format('YYYY-MM-DD')
        const columnNum = (this.chart.columns || []).length - 1

        this.units$.pipe(take(1)).subscribe(data => {
          const index = data.findIndex(row => row[0] === ymd)

          if (index === -1) {
            return
          }

          const selections = range(0, columnNum).map(i => ({
            row: index,
            column: i + 1,
          }))

          googleChart.setSelection(selections)
        })
      })
    )

    if (this.media.isActive('lt-md')) {
      this.unitsOptions.legend.position = 'none'
      this.unitsOptions.chartArea.left = 45
    } else {
      this.unitsOptions.legend.position = 'top'
      this.unitsOptions.chartArea.left = 80
    }
  }

  private getIniData() {
    this.checkedSubAccounts = []
    this.checkLabels = [
      { name: 'フィジカル消化数（実売数）', completed: true, disabled: false },
      { name: 'ストリーム数', completed: true, disabled: false },
      { name: 'ダウンロード数', completed: true, disabled: false },
    ]

    this.checkLabels.forEach(it => {
      if (it.completed) {
        this.checkedSubAccounts.push(it.name)
      }
    })
    this.subAccounts$ = of(['日付'].concat(this.checkedSubAccounts))
    this.units$ = this.store
      .select(getUnits)
      .pipe(
        map(it => it.map(unit => [unit.transactionDate, unit.pos, unit.streams, unit.downloads]))
      )

    // 取得先と色をマッピングして、グラフのオプションを設定する
    this.subAccounts$
      .pipe(
        map(it => {
          // 取得先と色をマッピング
          return this.mappingSourceAndColor(it)
        })
      )
      .subscribe(val => {
        // 色のオプションを取得する
        this.colorsOption = []
        val.forEach(d => this.colorsOption.push(d))

        // グラフのオプションを設定する
        this.unitsOptions = {
          ...lineChartStyle3Lines,
          colors: this.colorsOption,
        }
      })
  }

  // 選択したsubAccountsのunitsを表示する
  onChange() {
    // 一つのcheckboxだけをチェックしたら、disableする
    this.disableCheckbox()

    this.checkedSubAccounts = []

    this.checkLabels.forEach(it => {
      if (it.completed) {
        this.checkedSubAccounts.push(it.name)
      }
    })

    // 選択したsubAccountsを再取得する
    this.subAccounts$ = of(['日付'].concat(this.checkedSubAccounts))

    // 選択したsubAccountsのunitsを再取得する
    this.units$ = this.store.select(getUnits).pipe(
      map(it => {
        return this.convertSelected2ChartData(it)
      })
    )

    // 選択したsubAccountsの色を再設定する
    this.subAccounts$
      .pipe(
        map(it => {
          // 取得先と色をマッピング
          return this.mappingSourceAndColor(it)
        })
      )
      .subscribe(val => {
        // 色のオプションを取得する
        this.colorsOption = []
        val.forEach(d => this.colorsOption.push(d))

        // グラフのオプションを設定する
        this.unitsOptions = {
          ...lineChartStyle3Lines,
          colors: this.colorsOption,
        }
      })
  }

  private disableCheckbox() {
    var count = 0
    for (var i = 0; i < this.checkLabels.length; i++) {
      if (this.checkLabels[i].completed) {
        count++

        if (this.checkLabels[i].disabled) {
          this.checkLabels[i].disabled = false
        }
      }
    }
    if (count === 1) {
      for (var i = 0; i < this.checkLabels.length; i++) {
        if (this.checkLabels[i].completed) {
          this.checkLabels[i].disabled = true
        }
      }
    }
  }

  private convertSelected2ChartData(downloadUnits: Units[]): any[] {
    const dates = [...new Set(downloadUnits.map(item => item.transactionDate))]
    const column = []
    dates.forEach(d => {
      const data: any[] = [d]
      column.push(data)
      downloadUnits.map(units => {
        if (d === units.transactionDate) {
          this.checkedSubAccounts.forEach(item => {
            if (item === 'フィジカル消化数（実売数）') {
              data.push(units.pos)
            } else if (item === 'ストリーム数') {
              data.push(units.streams)
            } else if (item === 'ダウンロード数') {
              data.push(units.downloads)
            }
          })
        }
      })
    })
    return column
  }

  ngAfterViewInit() {
    if (!this.container) {
      return
    }
    this.widthWhenClosed = this.container.nativeElement.offsetWidth - 20
    this.widthWhenOpened = this.widthWhenClosed - 300 /* 右ドロワーの幅に合わせて！ */
  }

  ngOnDestroy() {
    this.subscriptions.forEach(it => it.unsubscribe())
  }

  onResize() {
    if (!this.chart) {
      return
    }
    // リサイズ時、出来事の開閉状態によって算出が異なる
    if (this.widthOpened) {
      this.widthWhenOpened = this.container.nativeElement.offsetWidth - 20
      this.widthWhenClosed = this.widthWhenOpened + 300
      this.chart.width = this.widthWhenOpened
    } else {
      this.widthWhenClosed = this.container.nativeElement.offsetWidth - 20
      this.widthWhenOpened = this.widthWhenClosed - 300
      this.chart.width = this.widthWhenClosed
    }
    this.redrawChart()
  }

  onSelect(event: ChartSelectionChangedEvent): void {
    const selected = event.selection && event.selection[0]

    if (!selected || selected.row == null) {
      return
    }

    const selectedRow = selected.row

    this.units$.pipe(take(1)).subscribe(units => {
      if (!units[selectedRow]) {
        return
      }

      this.isHandset$.pipe(take(1)).subscribe(isHandset => {
        if (isHandset) {
          return
        }

        this.store.dispatch(openEventDrawer())
        this.store.dispatch(
          selectEventDate({
            eventDate: units[selectedRow][0],
          })
        )
      })
    })
  }

  // アルバム換算数の取得先と色をマッピング処理
  private mappingSourceAndColor(subAccounts: string[]): string[] {
    const colors = []
    subAccounts.forEach(s => {
      switch (s) {
        case 'フィジカル消化数（実売数）': {
          colors.push('#ffc107')
          break
        }
        case 'ストリーム数': {
          colors.push('#e040fb')
          break
        }
        case 'ダウンロード数': {
          colors.push('#4caf50')
          break
        }
      }
    })
    return colors
  }

  private redrawChart(): void {
    if (!this.chart) {
      return
    }

    this.chart.ngOnChanges({
      width: new SimpleChange(undefined, this.chart.width, false),
    })
  }
}
